<template>
  <div class="documents-revision-history-container">
    <router-view name="sidebar" @hidden="navigateToRevisionHistory" />
    <document-secure-toolbar />
    <div class="aula-document-history-container page-layout-padding">
      <h1 class="document-title">
        <b-btn variant="link" class="file-document" :to="{ name: 'documents' }">
          {{ $getText('DOCUMENTS_TYPES_PAGE_TITLE') }}
        </b-btn>
        <span class="breadcumb-spacer">/</span>
        <b-btn variant="link" class="file-document" :to="{ name: 'documentsSecureList' }">
          {{ $getText('DOCUMENTS_TYPES_SECURE_FILES_TITLE') }}
        </b-btn>
        <span class="breadcumb-spacer">/</span>
        <b>{{ documentTitle }}</b>
      </h1>
      <ObservingContainer :options="observerOptions">
        <div class="table">
          <div class="table-row header">
            <div class="table-cell text-left">
              {{ $getText('DOCUMENTS_HISTORY_USER') }}
            </div>
            <div class="table-cell text-left">
              {{ $getText('DOCUMENTS_HISTORY_EDITION') }}
            </div>
            <div class="table-cell text-left">
              {{ $getText('DOCUMENTS_HISTORY_CHANGE_TYPE') }}
            </div>
            <div class="table-cell text-left">
              {{ $getText('DOCUMENTS_HISTORY_VERSION') }}
            </div>
          </div>
          <div v-for="(item, index) in revisions" :key="index" class="table-row body">
            <div class="table-cell not-collapsed">
              {{ item.createdBy }}
            </div>
            <div class="table-cell date not-collapsed">
              <nobr>{{ formattedCreatedDate(item) }}</nobr>
            </div>
            <div class="table-cell not-collapsed">
              {{ getRevisionChangeTypeText(item) }}
            </div>
            <div class="table-cell not-collapsed">
              <div
                v-if="item.isAvailable"
                class="media-data-container"
                @click="onRevisionClick(item.id)"
                @keydown.enter="onRevisionClick(item.id)"
              >
                <div tabindex="0" class="album-media-data" :aria-label="documentTitle">
                  <div class="media-item">
                    <div class="media-thumbnail">
                      <i class="icon-Aula_note" />
                    </div>
                    <div class="media-name">
                      {{ item.title }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                {{ $getText('DOCUMENTS_HISTORY_NOT_AVAILABLE') }}
              </div>
            </div>
            <div class="table-cell collapsed-only">
              <div>
                <strong>{{ $getText('DOCUMENTS_HISTORY_DATE') }}:</strong>
                {{ formattedCreatedDate(item) }}
              </div>
              <div>
                <strong>{{ $getText('DOCUMENTS_HISTORY_USER') }}:</strong>
                {{ item.createdBy }}
              </div>
              <div>
                <strong>{{ $getText('DOCUMENTS_HISTORY_CHANGE_TYPE') }}:</strong>
                {{ getRevisionChangeTypeText(item) }}
              </div>
              <div
                v-if="item.isAvailable"
                class="media-data-container"
                @click="onRevisionClick(item.id)"
                @keydown.enter="onRevisionClick(item.id)"
              >
                <div class="album-media-data">
                  <div class="media-item">
                    <div class="media-thumbnail">
                      <i class="icon-Aula_note" />
                    </div>
                    <div class="media-name">
                      {{ documentTitle }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <aula-spinner v-if="isLoading" />
        <ObservedTarget v-if="revisions.length > 0" @onIntersecting="onBottomReached" />
      </ObservingContainer>
    </div>
  </div>
</template>
<script>
import { types } from '../../store/types/types';
import { mapActions } from 'vuex';
import { mapGetters } from 'vuex';
import { documentTypes } from '../../../shared/enums/documentTypes';
import { revisionChangeTypeEnum } from '../../../shared/enums/RevisionChangeTypeEnum';
import DocumentSecureToolbar from './DocumentSecureToolbar.vue';
import ObservingContainer from '../../../shared/libs/intersection-observer/components/Container.vue';
import ObservedTarget from '../../../shared/libs/intersection-observer/components/Target.vue';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import { useUserProfileStore } from '../../../shared/stores';

export default {
  data: function () {
    return {
      documentTitle: '',
      isLoading: true,
      documentTypes,
      paginationStart: 0,
      pageSize: 20,
      revisionChangeTypeMap: new Map([
        [revisionChangeTypeEnum.CREATED, 'document.revision_created'],
        [revisionChangeTypeEnum.EDITED, 'document.revision_edited'],
        [revisionChangeTypeEnum.DELETED, 'document.revision_deleted'],
        [revisionChangeTypeEnum.EXPORTED, 'document.revision_exported'],
        [revisionChangeTypeEnum.RESTORED, 'document.revision_restored'],
        [revisionChangeTypeEnum.SHARED, 'document.revision_shared'],
        [revisionChangeTypeEnum.UNSHARED, 'document.revision_unshared'],
        [revisionChangeTypeEnum.IMPLICIT_UNSHARED, 'document.revision_implicit_unshared'],
        [revisionChangeTypeEnum.UNLOCKED, 'document.revision_unlocked'],
        [revisionChangeTypeEnum.LOCKED, 'document.revision_locked'],
        [revisionChangeTypeEnum.EDIT_PERMISSION_ADDED, 'document.revision_permission_added'],
        [revisionChangeTypeEnum.EDIT_PERMISSION_REMOVED, 'document.revision_permission_removed'],
        [revisionChangeTypeEnum.JOURNALIZED_TO_ESDH, 'document.revision_journalized_to_esdh'],
      ]),
    };
  },
  computed: {
    ...mapGetters({
      isMobile: types.GET_IS_MOBILE,
      liveDocument: types.GET_DOCUMENTS_LIVE,
      revisionData: types.GET_DOCUMENTS_REVISION,
    }),
    userProfileStore() {
      return useUserProfileStore();
    },
    profile() {
      return this.userProfileStore.institutionProfile;
    },
    observerOptions() {
      return { rootMargin: '50%' };
    },
    revisions() {
      return this.revisionData?.documentRevisionDtos || [];
    },
    hasMoreRevisions() {
      if (!this.revisionData) {
        return false;
      }
      return this.revisionData.totalCount > (this.paginationStart + 1) * this.pageSize;
    },
  },
  methods: {
    formattedCreatedDate(item) {
      return DateTimeUtil.shortDate(item.createdAt, true, true);
    },
    onBottomReached() {
      if (this.isLoading || !this.hasMoreRevisions) {
        return;
      }
      this.getMoreRevisions();
    },
    getNameAndMeta(person) {
      return this.$getProfileDisplayName(person);
    },
    async getRevisions() {
      const documentId = this.$route.params.documentId;
      const payload = {
        documentId,
        offset: this.pageSize * this.paginationStart,
        limit: this.pageSize,
      };
      this.isLoading = true;
      if (this.paginationStart === 0) {
        await this.loadRevisions(payload);
      } else {
        await this.loadMoreRevisions(payload);
      }
      this.isLoading = false;
    },
    onRevisionClick(revisionId) {
      this.$router.push({
        name: 'revisionHistoryDocument',
        params: { revisionId },
      });
    },
    navigateToRevisionHistory() {
      this.$router.push({ name: 'documentsRevisionHistory' });
    },
    getMoreRevisions() {
      this.paginationStart++;
      this.getRevisions();
    },
    getRevisionChangeTypeText(item) {
      const changedUsers = (item.sharedWith.length > 0 ? item.sharedWith : item.unsharedWith)
        .map(item => this.getNameAndMeta(item))
        .join(', ');
      let changeTypeText = '';

      switch (item.changeType) {
        case revisionChangeTypeEnum.SHARED:
        case revisionChangeTypeEnum.UNSHARED:
        case revisionChangeTypeEnum.IMPLICIT_UNSHARED:
        case revisionChangeTypeEnum.EDIT_PERMISSION_ADDED:
        case revisionChangeTypeEnum.EDIT_PERMISSION_REMOVED:
          changeTypeText = this.$t(this.revisionChangeTypeMap.get(item.changeType), { userName: changedUsers });
          break;
        case revisionChangeTypeEnum.JOURNALIZED_TO_ESDH:
          changeTypeText = this.$t(this.revisionChangeTypeMap.get(item.changeType), {
            recipientName: item.recipientName,
          });
          break;
        default:
          changeTypeText = this.$t(this.revisionChangeTypeMap.get(item.changeType));
          break;
      }

      return changeTypeText;
    },
    ...mapActions({
      loadRevisions: types.ACTIONS_GET_DOCUMENT_REVISIONS,
      loadMoreRevisions: types.ACTIONS_GET_MORE_DOCUMENT_REVISIONS,
      loadInternalDocument: types.ACTION_LOAD_INTERNAL_DOCUMENT,
      loadExternalDocument: types.ACTION_LOAD_EXTERNAL_DOCUMENT,
    }),
  },
  created() {
    const id = this.$route.params.documentId;
    const documentType = this.$route.params.documentType;
    const params = { id };
    if (documentType === documentTypes.EXTERNAL) {
      this.loadExternalDocument(params).then(response => {
        this.documentTitle = response.title;
      });
    } else {
      this.loadInternalDocument(params).then(response => {
        this.documentTitle = response.title;
      });
    }
    this.getRevisions();
  },
  components: {
    ObservedTarget,
    ObservingContainer,
    'document-secure-toolbar': DocumentSecureToolbar,
  },
};
</script>
<style scoped lang="scss">
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;
@use '../../../shared/assets/scss/components/documents/_overview' as *;

.show-more {
  padding: 20px 0;
  text-align: center;
  button {
    float: none;
    margin-left: 0;
  }
}
.document-title {
  flex-wrap: wrap;
}
.aula-document-history-container {
  .media-data-container {
    cursor: pointer;
    @include breakpoint-xl-down() {
      margin-top: 5px;
    }
    .album-media-data {
      background-color: $color-grey-light;
      flex: unset;
    }
  }
  @include breakpoint-lg-down() {
    padding: 15px;
    padding-bottom: 50px !important;
  }
  @include breakpoint-sm-down() {
    padding: 10px;
  }
  h1 {
    font-size: 18px;
    font-weight: 400;
    margin-bottom: 40px;
  }
  .breadcrumbs {
    cursor: pointer;
  }
  .breadcumb-spacer {
    padding: 0 10px;
    display: inline-block;
  }
  .document-history-title-header {
    background-color: $color-grey-dark;
    padding: 0;
    margin: 0;
    h5 {
      margin-bottom: 0;
      line-height: 60px;
    }
    .icon-Aula_back-arrow {
      font-size: 1.5em;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    .icon-header {
      color: $color-bg-black;
      cursor: pointer;
      @include breakpoint-lg() {
        display: inline-block;
        padding: 20px 0px 15px 0;
        margin-right: -17px;
      }
    }
  }
  .table {
    border-collapse: collapse;
    display: table;
    width: 100%;
    font-size: 14px;
    .table-row {
      background: #fff;
      display: table-row;
      .not-collapsed {
        display: none !important;

        @include breakpoint-xl() {
          display: table-cell !important;
        }
      }
      .collapsed-only {
        display: block !important;

        @include breakpoint-xl() {
          display: none !important;
        }
      }
      &.header {
        @include breakpoint-xl-down() {
          display: none;
        }
        background-color: $color-grey-dark;
        .table-cell {
          display: table-cell;
          padding: 15px;
          border-right: solid 2px transparent;
          border-top: solid 2px $color-grey-light;
          border-bottom: solid 2px $color-grey-light;
          text-transform: uppercase;
          text-align: center;
        }
      }
      &.body {
        .table-cell {
          border: solid 2px $color-grey-light;
          display: table-cell;
          padding: 15px;
          &.icon {
            background-color: $color-grey-base;
            vertical-align: middle;
            text-align: center;
            padding-top: 20px;
          }
          b {
            font-weight: 900;
          }
          .icon-Aula_elipses {
            display: block;
            margin-bottom: 15px;
            cursor: pointer;
          }
          .icon-Aula_write {
            display: block;
            cursor: pointer;
          }
        }
        &:hover {
          .table-cell {
            background: $color-grey-lighter;
            &.icon {
              background: $color-grey-dunkel;
              .icons {
                .user {
                  background: $color-grey-dunkel;
                  border: solid 3px $color-grey-dunkel;
                }
              }
            }
            b {
              font-weight: 900;
            }
          }
        }
      }
    }
  }
}

.aula-document-history-container {
  .table {
    .table-row {
      &.header {
        .table-cell {
          &.icon {
            span {
              .theme-guardian & {
                background-color: $color-primary-light-guardian;
              }
              .theme-employee & {
                background-color: $color-primary-light-employee;
              }
              .theme-child & {
                background-color: $color-primary-light-child;
              }
            }
          }
        }
      }
    }
  }
}
</style>
